import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, Col, Button } from "reactstrap";
import { toast } from "react-toastify";
import api from "../../../../services/api";
import CommonCardHeader from "../../../../Utils/CommonComponents/CommonCardHeader";
import DataTable from "react-data-table-component";
import { baseUrl } from "../../../../Utils/Baseurl/Baseurl";
import Papa from "papaparse";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

interface Token {
  tokenid: string;
  tokenregistration: {
    tokenno: number;
    prefix: string;
    appointmentdate: string;
    id:string;
  };
  medicalrep: {
    name: string;
    Company: {
      companyname: string;
    };
  };
  medicinename: string;
  contents?: { name: string; quantity: number }[];
  offerdetails: string;
  taxpercentage: string;
}

const MedicineHistory: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialDate = queryParams.get("date") || "";
  const [selectedDate, setSelectedDate] = useState<string>(initialDate);
  const [tokens, setTokens] = useState<Token[]>([]);

  useEffect(() => {
    fetchTokens();
  }, [selectedDate]);

  const fetchTokens = async () => {
    try {
      const response = await api.get(`${baseUrl}medicinedetails/approved`);
      console.log("API Response:", response);
      setTokens(response.data.data || []);
    } catch (error) {
      console.error("Error fetching medicine details:", error);
      toast.error("Failed to fetch medicine details.");
    }
  };

  const handleViewCertificates = (tokenid: string) => {
    if (!tokenid) {
      toast.error("Invalid token ID.");
      return;
    }
    navigate(`${process.env.PUBLIC_URL}/allcertificates/${tokenid}`);
  };

  const exportPDF = () => {
    if (!tokens.length) {
      toast.error("No data available for export.");
      return;
    }

    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text("Approved Medicine History", 14, 15);

    const tableColumn = [
      "#",
      "Token No",
      "Medical Rep",
      "Medicine Name",
      "Company Name",
      "Appointment Date",
      "Contents",
      "Tax Percentage",
      "Offer Details",
    ];

    const tableRows = tokens.map((token, index) => [
      index + 1,
      `${token.tokenregistration?.prefix ?? ""} ${token.tokenregistration?.tokenno ?? "-"}`,
      token.medicalrep?.name || "-",
      token.medicinename || "-",
      token.medicalrep?.Company?.companyname || "-",
      token.tokenregistration?.appointmentdate
        ? new Date(token.tokenregistration.appointmentdate).toLocaleDateString("en-GB")
        : "-",
      token.contents?.map((c) => `${c.name}: ${c.quantity}g`).join(", ") || "-",
      token.taxpercentage !== undefined ? `${token.taxpercentage}%` : "-",
      token.offerdetails || "-",
    ]);

    autoTable(doc, { head: [tableColumn], body: tableRows, startY: 25 });
    doc.save("Approved_Medicine_History.pdf");
  };

  const exportCSV = () => {
    if (!tokens.length) {
      toast.error("No data available for export.");
      return;
    }

    const csvData = tokens.map((token, index) => ({
      "#": index + 1,
      "Token No": `${token.tokenregistration?.prefix ?? ""} ${token.tokenregistration?.tokenno ?? "-"}`,
      "Medical Rep": token.medicalrep?.name || "-",
      "Medicine Name": token.medicinename || "-",
      "Company Name": token.medicalrep?.Company?.companyname || "-",
      "Appointment Date": token.tokenregistration?.appointmentdate || "-",
      "Contents": token.contents?.map((c) => `${c.name}:${c.quantity}g`).join(", ") || "-",
      "Tax Percentage": token.taxpercentage !== undefined ? `${token.taxpercentage}%` : "-",
      "Offer Details": token.offerdetails || "-",
    }));

    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "Approved_Medicine_History.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div>
      <Col sm={12}>
        <Card>
          <CommonCardHeader title="Approved Medicine Details" />
          <div className="table-responsive">
            <Button color="primary" className="mb-3" onClick={exportCSV}>
              Export to CSV
            </Button>
            <Button color="danger" className="mb-3 ms-2" onClick={exportPDF}>
              Export to PDF
            </Button>
            <DataTable
              columns={[
                {
                  name: "#",
                  selector: (_row, rowIndex) => (rowIndex !== undefined ? rowIndex + 1 : "-"),
                  sortable: true,
                },
                {
                  name: "Token No",
                  selector: (row) =>
                    `${row.tokenregistration?.prefix ?? ""} ${row.tokenregistration?.tokenno ?? "-"}`,
                  sortable: true,
                },
                {
                  name: "Appointment Date",
                  selector: (row) =>
                    row.tokenregistration?.appointmentdate
                      ? new Date(row.tokenregistration.appointmentdate).toLocaleDateString("en-GB")
                      : "-",
                  sortable: true,
                },
                { name: "Medical Rep", selector: (row) => row.medicalrep?.name || "-", sortable: true },
                { name: "Medicine Name", selector: (row) => row.medicinename || "-", sortable: true },
                { name: "Company Name", selector: (row) => row.medicalrep?.Company?.companyname || "-", sortable: true },
                {
                  name: "Contents",
                  selector: (row) =>
                    row.contents?.map((c) => `${c.name}: ${c.quantity}g`).join(", ") || "-",
                  sortable: false,
                },
                {
                  name: "Tax Percentage",
                  selector: (row) => (row.taxpercentage !== undefined ? `${row.taxpercentage}%` : "-"),
                  sortable: true,
                },
                { name: "Offer Details", selector: (row) => row.offerdetails || "-", sortable: true },
                {
                  name: "Actions",
                  cell: (row) => {
                    console.log("Row data:", row); // <-- Add this for debugging
                    return (
                      <Button color="info" onClick={() => handleViewCertificates(row.tokenregistration.id)}>
                        View Certificates
                      </Button>
                    );
                  }
                },
              ]}
              data={tokens}
              pagination
              highlightOnHover
              striped
            />
          </div>
        </Card>
      </Col>
    </div>
  );
};

export default MedicineHistory;
